/**
 * Merge a subset of a `getNodeNeighbors` response into the host view's
 * already-loaded graph data, deduping by node / edge id.
 *
 * Only the neighbour ids in `selectedIds` (and the host node's own id)
 * are candidates; edges are kept when both endpoints are either already
 * in the host data or in the selected set.
 */

import type { NeighborEdge, NeighborNode } from './types'

export interface MergeInput {
  hostNodes: NeighborNode[]
  hostEdges: NeighborEdge[]
  neighborResponse: { nodes: NeighborNode[]; relationships: NeighborEdge[] }
  /** Ids of neighbour nodes the user has staged. Must NOT include the host node id. */
  selectedIds: Set<string>
  /** Host node id (always kept as an endpoint context when filtering edges). */
  hostNodeId: string
}

export interface MergeResult {
  /** All nodes (host nodes ∪ newly-added staged neighbours). Deduped by id. */
  nodes: NeighborNode[]
  /** All edges (host edges ∪ newly-added edges). Deduped by id. */
  edges: NeighborEdge[]
  /** The newly-added subset — useful for success toasts. */
  addedNodes: NeighborNode[]
  addedEdges: NeighborEdge[]
}

export function mergeNeighborsIntoGraph({
  hostNodes,
  hostEdges,
  neighborResponse,
  selectedIds,
  hostNodeId,
}: MergeInput): MergeResult {
  const existingNodeIds = new Set(hostNodes.map(n => n.id))
  const existingEdgeIds = new Set(hostEdges.map(e => e.id))

  const addedNodes: NeighborNode[] = []
  for (const n of neighborResponse.nodes) {
    if (n.id === hostNodeId) continue
    if (!selectedIds.has(n.id)) continue
    if (existingNodeIds.has(n.id)) continue
    addedNodes.push(n)
    existingNodeIds.add(n.id)
  }

  // After staging, an edge is includable when:
  // - it's not already in the graph,
  // - both endpoints are either in the host graph or in the staged set (post-merge)
  const validEndpointIds = existingNodeIds // already merged above
  const addedEdges: NeighborEdge[] = []
  for (const e of neighborResponse.relationships) {
    if (existingEdgeIds.has(e.id)) continue
    if (!validEndpointIds.has(e.source) || !validEndpointIds.has(e.target)) continue
    addedEdges.push(e)
    existingEdgeIds.add(e.id)
  }

  return {
    nodes: [...hostNodes, ...addedNodes],
    edges: [...hostEdges, ...addedEdges],
    addedNodes,
    addedEdges,
  }
}
